'use client';

import FaIcon, { faChevronDown, faChevronUp } from '@/components/icon/FaIcon/FaIcon';
import Accordion from '@/components/ui/Accordion/Accordion';
import utilsStyles from '@/styles/modules/utils.module.scss';
import { scrollIntoTheView } from '@/utils/functions';
import { useAtom } from 'jotai';
import Link from 'next/link';
import { useEffect, useMemo, useState } from 'react';
import { localeAtom } from '../../atoms/localAtom';
import { pickText } from '../../utils/pickText';
import styles from './LotteryDetailDescription.module.scss';

function LotteryDetailDescription({
  precautions,
  description,
  shippingDescription,
  benefitDescription,
  wchanceAbout,
  precautions_en,
  description_en,
  shippingDescription_en,
  benefitDescription_en,
  wchanceAbout_en,
  descriptionTitle = 'くじ詳細',
  descriptionTitle_en = 'Lottery Details',
  howToId,
  isDefaultOpen = false,
  isFamikuji = false,
  isEnabledTrade = false,
  lotteryId,
}: {
  precautions: string;
  description: string;
  shippingDescription: string;
  benefitDescription?: string;
  wchanceAbout?: string;
  precautions_en?: string;
  description_en?: string;
  shippingDescription_en?: string;
  benefitDescription_en?: string;
  wchanceAbout_en?: string;
  descriptionTitle?: string;
  descriptionTitle_en?: string;
  howToId?: string;
  isDefaultOpen?: boolean;
  isFamikuji?: boolean;
  isEnabledTrade?: boolean;
  lotteryId?: string;
}) {
  const [locale] = useAtom(localeAtom);
  const [isOpened, setIsOpened] = useState(isDefaultOpen);
  const [transLateItem, setTransLateItem] = useState({
    title: '',
    description: '',
    precautions: '',
    shippingDescription: '',
    benefitDescription: '',
    wchanceAbout: '',
  });
  const [contentUpdated, setContentUpdated] = useState(0);

  useEffect(() => {
    setTransLateItem({
      title: pickText(locale, descriptionTitle, descriptionTitle_en ?? ''),
      description: pickText(locale, description, description_en ?? ''),
      precautions: pickText(locale, precautions, precautions_en ?? ''),
      shippingDescription: pickText(locale, shippingDescription, shippingDescription_en ?? ''),
      benefitDescription: pickText(locale, benefitDescription ?? '', benefitDescription_en ?? ''),
      wchanceAbout: pickText(locale, wchanceAbout ?? '', wchanceAbout_en ?? ''),
    });

    setContentUpdated((prev) => prev + 1);
  }, [
    locale,
    descriptionTitle,
    descriptionTitle_en,
    description,
    description_en,
    precautions,
    precautions_en,
    shippingDescription,
    shippingDescription_en,
    benefitDescription,
    benefitDescription_en,
    wchanceAbout,
    wchanceAbout_en,
  ]);

  const toggleAccordion = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsOpened(!isOpened);
  };
  const goHowTo = (e: React.MouseEvent, targetId: string) => {
    e.preventDefault();
    scrollIntoTheView(targetId);
  };
  const accordionKey = useMemo(() => contentUpdated, [contentUpdated]);

  const isDescriptionHidden =
    !transLateItem.description || transLateItem.description.trim() === '' || transLateItem.description === '<p>　</p>';

  return (
    <>
      <div className={styles.componentStyle}>
        <p className={`${utilsStyles.textLarge} ${utilsStyles.textBold}`}>{transLateItem.title}</p>
        <hr className={styles.itemHr} />
        <div className={utilsStyles.textSmall}>
          <Accordion isOpened={isOpened} key={accordionKey} startHeight={50}>
            <div
              className={`${utilsStyles.textPrewrap} ${utilsStyles.textHtml} ${isDescriptionHidden ? styles.hidden : ''}`}
              // biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation>
              dangerouslySetInnerHTML={{ __html: transLateItem.description }}
            />
            {precautions && (
              <>
                <p className={`${isDescriptionHidden ? '' : utilsStyles.mtXSmall} ${utilsStyles.textMedium}`}>
                  <b>{locale === 'en' ? 'Precautions' : '注意事項'}</b>
                </p>
                <div
                  className={`${utilsStyles.textPrewrap} ${utilsStyles.textHtml}`}
                  // biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation>
                  dangerouslySetInnerHTML={{ __html: transLateItem.precautions }}
                />
              </>
            )}
            {shippingDescription && (
              <>
                <p className={`${utilsStyles.mtXSmall} ${utilsStyles.textMedium}`}>
                  <b>{locale === 'en' ? 'Shipping Instructions' : '配送について'}</b>
                </p>
                <div
                  className={`${utilsStyles.textPrewrap} ${utilsStyles.textHtml}`}
                  // biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation>
                  dangerouslySetInnerHTML={{ __html: transLateItem.shippingDescription }}
                />
              </>
            )}
            {benefitDescription && (
              <>
                <p className={`${utilsStyles.mtXSmall} ${utilsStyles.textMedium}`}>
                  <b>{locale === 'en' ? 'Benefits' : '特典について'}</b>
                </p>
                <div
                  className={`${utilsStyles.textPrewrap} ${utilsStyles.textHtml}`}
                  // biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation>
                  dangerouslySetInnerHTML={{ __html: transLateItem.benefitDescription }}
                />
              </>
            )}
            {wchanceAbout && (
              <>
                <p className={`${utilsStyles.mtXSmall} ${utilsStyles.textMedium}`}>
                  <b>{locale === 'en' ? 'W Chance' : 'Wチャンス賞について'}</b>
                </p>
                <div
                  className={`${utilsStyles.textPrewrap} ${utilsStyles.textHtml}`}
                  // biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation>
                  dangerouslySetInnerHTML={{ __html: transLateItem.wchanceAbout }}
                />
              </>
            )}
          </Accordion>

          <div className={utilsStyles.centered}>
            {/* biome-ignore lint/a11y/useValidAnchor: <explanation> */}
            <a
              href='#'
              onClick={(e) => {
                toggleAccordion(e);
              }}
            >
              {isOpened ? (locale === 'en' ? 'close' : '閉じる') : locale === 'en' ? 'show more' : 'もっと見る'}
            </a>
            {isOpened ? <FaIcon icon={faChevronUp} /> : <FaIcon icon={faChevronDown} />}
          </div>
        </div>
      </div>
      {howToId && (
        <div className={`${styles.linkContainer} ${utilsStyles.mtSmall} ${utilsStyles.textLink} `}>
          <Link
            href='#'
            onClick={(e) => {
              goHowTo(e, howToId);
            }}
          >
            {isFamikuji ? 'くじの遊び方はコチラ' : 'RAFFLEの利用方法はコチラ'}
          </Link>
          {isEnabledTrade && lotteryId && <Link href={`/trade/${lotteryId}`}>景品交換の利用はコチラ</Link>}
        </div>
      )}
    </>
  );
}

export default LotteryDetailDescription;
